<script setup>
import { ref, onMounted, onUnmounted, watch, computed, provide } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import pipelineCardCable from 'dashboard/helper/pipelineCardCable';
import PipelineTab from './components/PipelineTab.vue';
import ListTab from './components/ListTab.vue';
import AgendaTab from './components/AgendaTab.vue';
import PipelinesManager from './components/PipelinesManager.vue';
import MessageTemplates from './components/MessageTemplates.vue';
import PipelineCardDetailView from './components/PipelineCardDetailView.vue';
import Spinner from 'shared/components/Spinner.vue';
import { usePipelinePermissions } from './composables/usePipelinePermissions';

const store = useStore();
const route = useRoute();
const router = useRouter();

const currentView = ref('pipeline');
const selectedItemId = ref(null);
const forceReloadCounter = ref(0);
const isTransitioning = ref(false); // Phase 1 - UX Improvements

// Helper functions for URL slugs
const generateSlug = pipeline => {
  if (!pipeline) return '';
  const name = pipeline.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${pipeline.id}-${name}`;
};

const extractIdFromSlug = slug => {
  if (!slug) return null;
  const match = slug.match(/^(\d+)/);
  // Return as string to match store IDs (pipelines store converts IDs to strings)
  return match ? match[1] : null;
};

// Map view names to URL paths and vice versa
// Note: 'dashboard' is handled separately - redirects to UnifiedPipelineDashboard
const viewToPath = {
  pipeline: 'board',
  list: 'list',
  agenda: 'agenda',
  pipelines: 'pipelines',
  templates: 'templates',
  'item-detail': 'item',
};

const pathToView = Object.fromEntries(
  Object.entries(viewToPath).map(([k, v]) => [v, k])
);

// Navigate to URL without triggering reload
const navigateToUrl = (pipelineSlug, view, itemId = null) => {
  const accountId = route.params.accountId;
  let routeName = 'pipeline_board';
  const params = { accountId };

  if (pipelineSlug) {
    params.pipelineSlug = pipelineSlug;
  }

  if (view) {
    params.view = viewToPath[view] || view;
  }

  if (itemId) {
    params.itemId = itemId;
    routeName = 'pipeline_item_detail';
  } else if (pipelineSlug && view) {
    routeName = 'pipeline_board';
  } else if (pipelineSlug) {
    routeName = 'pipeline_with_funnel';
  } else {
    routeName = 'pipeline_index';
  }

  router.push({ name: routeName, params });
};

const isLoading = computed(() => store.state.pipelineCards.loading);
const pipelineCards = computed(() => {
  // P0.4 — sem logger.debug aqui. Era .map(i => i.id) sobre 2000+ items
  // a cada re-eval do computed (alocava array novo de IDs descartado).
  return store.getters['pipelineCards/getPipelineCardItems'] || [];
});
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

// Permission guard — redirect to first accessible pipeline when user lacks access
const { canViewPipeline, isAdmin } = usePipelinePermissions(selectedPipeline);

watch(
  () => [selectedPipeline.value, canViewPipeline.value],
  ([pipeline, canView]) => {
    if (!pipeline || isAdmin.value) return;
    if (!canView) {
      // Try to find first accessible pipeline in the list
      const allPipelines = store.getters['pipelines/getPipelines'];
      const currentUser = store.getters.getCurrentUser;
      const accessible = allPipelines.find(p => {
        const agents = p.settings?.agents ?? [];
        return (
          agents.length === 0 || agents.some(a => a.id === currentUser?.id)
        );
      });
      if (accessible) {
        store.dispatch('pipelines/setSelectedPipeline', accessible);
      } else {
        // No accessible pipeline: show pipelines manager view
        currentView.value = 'pipelines';
      }
    }
  }
);

const labelsMap = ref({});

// Controle centralizado de carregamento
const isInitialLoad = ref(true);

// UX Card-Q1.2 (2026-04-22): centralizar leitura de localStorage.
// Antes cada PipelineCardItem lia 2× no onMounted (visibility +
// collapsed). Com 100 cards = 200 reads sincronas no boot do board.
// Agora PipelineView lê 1× e provê refs reativos via provide/inject.
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const collapsedStorageKey = computed(
  () =>
    `u${currentUserId.value}_a${currentAccountId.value}_pipeline_collapsed_items`
);

const safeReadJSON = key => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const cardVisibility = ref({});
const collapsedItems = ref({});

const loadCardVisibility = () => {
  cardVisibility.value = safeReadJSON('pipeline_cards_visibility');
};
const loadCollapsedItems = () => {
  collapsedItems.value = safeReadJSON(collapsedStorageKey.value);
};

const updateCardVisibility = (cardId, settings) => {
  cardVisibility.value = {
    ...cardVisibility.value,
    [cardId]: { ...cardVisibility.value[cardId], ...settings },
  };
  localStorage.setItem(
    'pipeline_cards_visibility',
    JSON.stringify(cardVisibility.value)
  );
};

const updateCollapsedItem = (cardId, isCollapsed) => {
  collapsedItems.value = {
    ...collapsedItems.value,
    [cardId]: isCollapsed,
  };
  localStorage.setItem(
    collapsedStorageKey.value,
    JSON.stringify(collapsedItems.value)
  );
};

// Bulk (Recolher/Expandir todos da coluna): um único write reativo +
// localStorage para N cards — o caminho antigo escrevia direto no storage,
// invisível ao ref injetado que os cards leem (bug "Recolher todos não faz
// nada", report do usuário 2026-06-10).
const updateCollapsedItems = entries => {
  collapsedItems.value = { ...collapsedItems.value, ...entries };
  localStorage.setItem(
    collapsedStorageKey.value,
    JSON.stringify(collapsedItems.value)
  );
};

loadCardVisibility();
loadCollapsedItems();

// Disponibilizar dados para componentes filhos
provide('labelsMap', labelsMap);
provide('cardVisibility', cardVisibility);
provide('collapsedItems', collapsedItems);
provide('updateCardVisibility', updateCardVisibility);
provide('updateCollapsedItem', updateCollapsedItem);
provide('updateCollapsedItems', updateCollapsedItems);

// Ciclo 3b — o board (kanban) carrega via paginação server-side POR COLUNA
// (fetchBoardByStages: stages visíveis em paralelo, limit 50/stage). As
// demais views (list/agenda/detail) seguem no fetch global flat, que continua
// existindo e compartilha o mesmo cache/flat.
const fetchCardsForCurrentView = async () => {
  if (currentView.value === 'pipeline') {
    await store.dispatch('pipelineCards/fetchBoardByStages');
  } else {
    await store.dispatch('pipelineCards/fetchPipelineCardItems');
  }
};

// Track previous pipeline ID to detect changes
const previousPipelineId = ref(null);

// Watch the computed selectedPipeline ID directly for more reliable reactivity
// Using the computed property ensures Vue's reactivity system tracks changes properly
watch(
  () => selectedPipeline.value?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Fix P0 — pipeline_index/with_funnel/board são route records DISTINTOS
      // com o mesmo componente → remount a cada navegação entre eles. No
      // disparo `immediate` do setup (oldId === undefined) o store global
      // ainda aponta para o pipeline da visita ANTERIOR; sem este guard, o
      // board disparava fetches inteiros para o pipeline stale antes de o
      // onMounted resolver o slug da URL (desperdício + corrida que prendia
      // colunas em "Carregando itens..."). Se a rota tem slug e ele difere,
      // retorna sem fetch: o onMounted seleciona o pipeline da rota e este
      // watcher dispara de novo com o id certo. Trocas iniciadas pelo usuário
      // (oldId definido) seguem direto — a URL é atualizada DEPOIS do fetch.
      if (oldId === undefined) {
        const routePipelineId = extractIdFromSlug(route.params.pipelineSlug);
        if (routePipelineId && String(routePipelineId) !== String(newId)) {
          return;
        }
      }

      // Update tracking ref
      previousPipelineId.value = newId;

      // Fetch cards for new pipeline (board → por stage; demais → global)
      await fetchCardsForCurrentView();
      isInitialLoad.value = false;

      // Update URL when pipeline changes (but not on initial load from URL)
      if (oldId) {
        const pipeline = store.getters['pipelines/getSelectedPipeline'];
        navigateToUrl(generateSlug(pipeline), currentView.value);
      }
    }
  },
  { immediate: true }
);

// Watch for route changes (browser back/forward)
watch(
  () => route.params,
  newParams => {
    const { pipelineSlug, view, itemId } = newParams;

    // Handle pipeline slug change
    if (pipelineSlug) {
      const pipelineId = extractIdFromSlug(pipelineSlug);
      const pipelines = store.getters['pipelines/getPipelines'];
      const pipeline = pipelines.find(p => p.id === pipelineId);

      if (pipeline && pipeline.id !== selectedPipeline.value?.id) {
        store.dispatch('pipelines/setSelectedPipeline', pipeline);
      }
    }

    // Handle view change - redirect to unified dashboard if 'dashboard'
    if (view) {
      if (view === 'dashboard') {
        const pipelineId = pipelineSlug
          ? extractIdFromSlug(pipelineSlug)
          : 'all';
        router.push({
          name: 'pipeline_dashboard_unified',
          query: { pipeline: pipelineId },
        });
        return;
      }

      const mappedView = pathToView[view] || view;
      if (currentView.value !== mappedView) {
        currentView.value = mappedView;
      }
    }

    // Handle item ID change
    if (itemId) {
      const parsedItemId = parseInt(itemId, 10);
      if (selectedItemId.value !== parsedItemId) {
        selectedItemId.value = parsedItemId;
        currentView.value = 'item-detail';
        forceReloadCounter.value += 1;
      }
    } else if (currentView.value === 'item-detail' && !itemId) {
      // If we're in item-detail but no itemId, go back to pipeline view
      currentView.value = 'pipeline';
      selectedItemId.value = null;
    }
  },
  { deep: true }
);

// Provide URL helper to child components
provide('generatePipelineSlug', generateSlug);
provide('navigateToUrl', navigateToUrl);

// Real-time WebSocket: Subscribe to pipeline changes when pipeline changes
watch(
  () => selectedPipeline.value?.id,
  newPipelineId => {
    // Only change pipeline if cable is initialized (will happen in onMounted)
    if (newPipelineId && pipelineCardCable.isInitialized()) {
      // Change subscription when pipeline changes (uses changePipeline internally)
      pipelineCardCable.changePipeline(newPipelineId);
    }
  }
);

// Modificar onMounted para usar o store e sincronizar com URL
onMounted(async () => {
  // 1. Buscar lista de pipelines primeiro
  await store.dispatch('pipelines/fetch');

  // 1.1 Buscar labels para exibir cores corretas nos cards.
  // Fix P0 — falha aqui NÃO pode abortar o onMounted: sem o restante do
  // fluxo, ninguém seleciona o pipeline da rota nem dispara o fetch do board
  // (tela "Nenhum item" sem nenhuma request).
  try {
    await store.dispatch('labels/get');
  } catch {
    // Cards renderizam sem cores de label; o board segue funcional.
  }
  const allLabels = store.getters['labels/getLabels'] || [];
  // Criar mapa de label title -> color para uso nos cards
  allLabels.forEach(label => {
    labelsMap.value[label.title] = label.color;
  });

  const pipelines = store.getters['pipelines/getPipelines'];
  const { pipelineSlug, view, itemId } = route.params;

  // 2. Se tiver slug na URL, selecionar o pipeline correspondente
  if (pipelineSlug) {
    const pipelineId = extractIdFromSlug(pipelineSlug);
    const pipeline = pipelines.find(p => p.id === pipelineId);

    if (pipeline) {
      await store.dispatch('pipelines/setSelectedPipeline', pipeline);
    }
  }

  // 3. Se tiver view na URL, definir a view
  if (view) {
    const mappedView = pathToView[view] || view;
    currentView.value = mappedView;
  }

  // 4. Se tiver itemId na URL, selecionar o item
  if (itemId) {
    selectedItemId.value = parseInt(itemId, 10);
    currentView.value = 'item-detail';
    forceReloadCounter.value += 1;
  }

  // 5. Se não houver pipeline selecionado e não houver slug na URL,
  // selecionar o primeiro pipeline acessível e atualizar URL
  if (!selectedPipeline.value && pipelines.length > 0) {
    const currentUser = store.getters.getCurrentUser;
    const userIsAdmin = currentUser?.role === 'administrator';
    const firstAccessible = userIsAdmin
      ? pipelines[0]
      : pipelines.find(p => {
          const agents = p.settings?.agents ?? [];
          return (
            agents.length === 0 || agents.some(a => a.id === currentUser?.id)
          );
        }) || pipelines[0];
    await store.dispatch('pipelines/setSelectedPipeline', firstAccessible);
    navigateToUrl(generateSlug(firstAccessible), currentView.value);
  } else if (selectedPipeline.value && !pipelineSlug) {
    // Se já tem pipeline selecionado mas não tem na URL, atualizar URL
    navigateToUrl(generateSlug(selectedPipeline.value), currentView.value);
  }

  // 6. Carregar os cards do pipeline selecionado.
  // Fix P0 — garantia determinística pós-remount: quando o pipeline da rota
  // JÁ era o selecionado no setup, o setSelectedPipeline do passo 2 é no-op e
  // o watcher (que só dispara em MUDANÇA de id) não busca de novo — este
  // dispatch é quem garante o load. Duplicidade é inócua: o guard de
  // dedupe/cache do fetchBoardByStages absorve o segundo dispatch.
  if (selectedPipeline.value?.id) {
    await fetchCardsForCurrentView();
    isInitialLoad.value = false;
  }

  // 7. Initialize real-time WebSocket connection for pipeline updates
  const currentUser = store.getters.getCurrentUser;
  if (currentUser?.pubsub_token) {
    pipelineCardCable.init(store, currentUser.pubsub_token);

    // Subscribe to selected pipeline if already available
    if (selectedPipeline.value?.id) {
      pipelineCardCable.subscribe(selectedPipeline.value.id);
    }
  }
});

// Cleanup WebSocket connection on unmount
onUnmounted(() => {
  pipelineCardCable.unsubscribe();
});

const switchView = async (view, updateUrl = true) => {
  // Redirect to unified dashboard when 'dashboard' is selected
  if (view === 'dashboard') {
    const pipelineId = selectedPipeline.value?.id || 'all';
    router.push({
      name: 'pipeline_dashboard_unified',
      query: { pipeline: pipelineId },
    });
    return;
  }

  // Phase 1 - UX Improvements: Add loading state during view transitions
  isTransitioning.value = true;

  // Minimum loading time for smooth UX (prevent flash)
  await Promise.all([
    new Promise(resolve => {
      setTimeout(resolve, 300);
    }),
    new Promise(resolve => {
      requestAnimationFrame(resolve);
    }),
  ]);

  currentView.value = view;
  selectedItemId.value = null; // Resetar o item selecionado ao trocar de view

  // Ciclo 3b — o board carrega 50/stage (parcial por design); o cache é
  // mode-aware ('board' vs 'global'). Sem este dispatch, trocar board→list
  // renderizaria o flat parcial silenciosamente (blocker do cross-review).
  fetchCardsForCurrentView();

  // Update URL when switching views
  if (updateUrl && selectedPipeline.value) {
    navigateToUrl(generateSlug(selectedPipeline.value), view);
  }

  // Wait for DOM update before hiding loading
  await new Promise(resolve => {
    requestAnimationFrame(resolve);
  });
  isTransitioning.value = false;
};

// Função para visualizar detalhes do item
const handleItemClick = item => {
  selectedItemId.value = item.id;
  forceReloadCounter.value += 1;
  currentView.value = 'item-detail';

  // Update URL with item detail
  if (selectedPipeline.value) {
    navigateToUrl(generateSlug(selectedPipeline.value), 'item-detail', item.id);
  }
};

// Voltar para a view anterior
const handleBackFromDetail = () => {
  currentView.value = 'pipeline'; // ou guardar a última view ativa
  selectedItemId.value = null;

  // Update URL to remove item detail
  if (selectedPipeline.value) {
    navigateToUrl(generateSlug(selectedPipeline.value), 'pipeline');
  }
};

// Simplificar para usar o store para atualizações
const handleItemsUpdated = async () => {
  await fetchCardsForCurrentView();
};

const getCurrentComponent = computed(() => {
  switch (currentView.value) {
    case 'pipeline':
      return PipelineTab;
    case 'list':
      return ListTab;
    case 'agenda':
      return AgendaTab;
    case 'pipelines':
      return PipelinesManager;
    case 'templates':
      return MessageTemplates;
    case 'item-detail':
      return PipelineCardDetailView;
    default:
      return PipelineTab;
  }
});
</script>

<template>
  <div class="flex h-full w-full overflow-auto relative">
    <!-- Loading Overlay (Phase 1 - UX Improvements) -->
    <Transition name="fade">
      <div
        v-if="isTransitioning"
        class="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center"
      >
        <Spinner size="small" color-scheme="primary" />
      </div>
    </Transition>

    <keep-alive>
      <component
        :is="getCurrentComponent"
        :current-view="currentView"
        :pipeline-cards="pipelineCards"
        :labels-map="labelsMap"
        :is-loading="isLoading"
        :item-id="selectedItemId"
        :force-reload="forceReloadCounter"
        @switch-view="switchView"
        @items-updated="handleItemsUpdated"
        @item-click="handleItemClick"
        @back="handleBackFromDetail"
        @item-updated="handleItemsUpdated"
      />
    </keep-alive>
  </div>
</template>

<style scoped>
.flex {
  min-height: 0;
  min-width: 0;
  height: 100%;
  width: 100%;
}

/* Fade transition for loading overlay (Phase 1 - UX Improvements) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
